import { makeObservable, observable, action, reaction, computed } from 'mobx';
import axios, { CancelTokenSource } from 'axios';
import { Subscription } from 'rxjs';
import { message } from 'antd';
import { RenewalSuggestionsStateDto, RenewalSuggestionDto, RenewalSuggestionRequest } from '../types';
import { Disposer, SettingsUtils } from '../../custom_shared/misc';
import { TypeUtils } from '../../common/misc';
import { InputIds, InstanceUtils, ChildNodeIds, NodeIds, NodeUtils, SectionIds } from '../misc';
import { ContractsIngestionService } from '../services';
import { InputBaseModel, LayersNodeModel, NodeBaseModel } from '../models';
import AppStore from './AppStore';
import NodesStore from './NodesStore';
import SessionStore from './SessionStore';
import ContractSubmissionStore from './ContractSubmissionStore';
import BaseCompanySegregationStore from './BaseCompanySegregationStore';

const sqlScriptIds = ['SqlScripts_RenewalSuggestionsScriptId'];

export default class RenewalSuggestionsStore extends Disposer {
    readonly defaultGroupKey = 'Suggestions';

    state: RenewalSuggestionsStateDto = {
        searchDisabled: false,
        groupedSuggestions: {}
    };

    selectedSuggestions: Map<string, RenewalSuggestionDto> = new Map();

    modalVisible: boolean = false;

    controlsVisible: boolean = false;

    searchingSuggestions: boolean = false;

    error: string = '';

    private hasInitializedControls: boolean = false;
    private cancelTokenSource: CancelTokenSource | null = null;
    private baseCompanyChangeSubscription: Subscription | null = null;

    constructor(
        private readonly appStore: AppStore,
        private readonly nodesStore: NodesStore,
        private readonly sessionStore: SessionStore,
        private readonly contractSubmissionStore: ContractSubmissionStore,
        private readonly baseCompanySegregationStore: BaseCompanySegregationStore,
        private readonly service: ContractsIngestionService
    ) {
        super();

        makeObservable(this, {
            state: observable,
            selectedSuggestions: observable,
            modalVisible: observable,
            controlsVisible: observable,
            searchingSuggestions: observable,
            error: observable,
            setState: action,
            setModalVisible: action,
            setControlsVisible: action,
            setSearchingSuggestions: action,
            setError: action,
            toggleSelectedSuggestion: action,
            isRenewalSupported: computed,
            isGroupedByLayer: computed,
            hasRenewalSuggestions: computed,
            defaultGroupSuggestions: computed
        });

        this.reactions.push(
            reaction(
                () => this.state.groupedSuggestions,
                () => {
                    this.selectedSuggestions.clear();

                    if (this.isGroupedByLayer) {
                        return;
                    }

                    if (this.defaultGroupSuggestions.length) {
                        this.selectedSuggestions.set(this.defaultGroupKey, this.defaultGroupSuggestions[0]);
                    }
                }
            )
        );

        this.reactions.push(
            reaction(
                () => ({
                    isInitializing: this.sessionStore.isInitializing,
                    isRenewalSupported: this.isRenewalSupported
                }),
                ({ isInitializing, isRenewalSupported }) => {
                    if (isInitializing || !isRenewalSupported || this.controlsVisible) {
                        return;
                    }

                    void this.getState().then(() => this.initialize());
                },
                { fireImmediately: true }
            )
        );

        this.baseCompanyChangeSubscription = this.baseCompanySegregationStore.baseCompanyChangeSubject.subscribe(() => {
            void this.handleBaseCompanyChange();
        });
    }

    private static createRenewalSuggestionsRequest(
        generalInformationInputs: InputBaseModel[],
        limitsInputs: InputBaseModel[],
        mainClassInputs: InputBaseModel[]
    ): RenewalSuggestionRequest | null {
        const {
            UnderwritingYear,
            BusinessType,
            PeriodStart,
            Cedent,
            Insured,
            Currency,
            MainClass,
            Broker,
            TypeOfParticipation
        } = InputIds.GeneralInformation;

        const { Cover, Excess } = InputIds.Limits;

        const underwritingYear = generalInformationInputs.find(i => i.id === UnderwritingYear)?.dto.value;
        const periodStart = generalInformationInputs.find(i => i.id === PeriodStart)?.dto.value;
        const businessType = generalInformationInputs.find(i => i.id === BusinessType)?.dto.value;
        const cedent = generalInformationInputs.find(i => i.id === Cedent)?.dto.value;
        const insured = generalInformationInputs.find(i => i.id === Insured)?.dto.value;
        const currency = generalInformationInputs.find(i => i.id === Currency)?.dto.value;
        const mainClass = mainClassInputs.find(i => i.id === MainClass)?.dto.value;
        const typeOfParticipation = generalInformationInputs.find(i => i.id === TypeOfParticipation)?.dto.value;
        const broker = generalInformationInputs.find(i => i.id === Broker)?.dto.value;
        const limitCover = limitsInputs.find(i => i.id === Cover)?.dto.value;
        const limitExcess = limitsInputs.find(i => i.id === Excess)?.dto.value;

        const suggestionsRequest: RenewalSuggestionRequest = {
            underwritingYear: TypeUtils.isNumber(underwritingYear) ? underwritingYear : undefined,
            typeOfBusiness: TypeUtils.isString(businessType) ? businessType : undefined,
            insuredPeriodStart: TypeUtils.isString(periodStart) ? periodStart : undefined,
            cedent: TypeUtils.isString(cedent) ? cedent : undefined,
            insured: TypeUtils.isString(insured) ? insured : undefined,
            currency: TypeUtils.isString(currency) ? currency : undefined,
            mainClasses: TypeUtils.isStringArray(mainClass) ? mainClass : [],
            typeOfParticipation: TypeUtils.isString(typeOfParticipation) ? typeOfParticipation : undefined,
            broker: TypeUtils.isString(broker) ? broker : undefined,
            limitCover: this.parseCoverValue(limitCover),
            limitExcess: TypeUtils.isNumber(limitExcess) ? limitExcess : undefined
        };

        if (Object.values(suggestionsRequest).every(value => value === undefined)) {
            return null;
        }

        return suggestionsRequest;
    }

    private static parseCoverValue(value: unknown) {
        if (!TypeUtils.isString(value)) {
            return undefined;
        }

        // Cover value also can be "Unlimited", for request we only use amount
        const parsedCover = parseFloat(value);
        return TypeUtils.isNumber(parsedCover) ? parsedCover : undefined;
    }

    private static setRenewalInputValues(inputs: InputBaseModel[], suggestion: RenewalSuggestionDto) {
        const businessIdInput = inputs.find(i => i.id === InputIds.GeneralInformation.BusinessId);
        const expiringPeriodStartInput = inputs.find(i => i.id === InputIds.GeneralInformation.ExpiringPeriodStart);

        if (businessIdInput && expiringPeriodStartInput && suggestion.businessId && suggestion.startDate) {
            const inputsToUpdate = [businessIdInput, expiringPeriodStartInput];
            const values = [suggestion.businessId, suggestion.startDate];
            const history = businessIdInput.ownerNode.history;
            history.editInputs(inputsToUpdate, (input, index) => input.setValue(values[index]));
        }
    }

    private static getMainSectionInputs(node: NodeBaseModel | undefined, layerId?: string) {
        return (
            NodeUtils.getSections(node, layerId).find(section => section.id === SectionIds.MainSection)?.inputs ?? []
        );
    }

    get isRenewalSupported() {
        return SettingsUtils.ensureSqlScriptIdsConfigured(this.sessionStore.settings, sqlScriptIds);
    }

    // A single layer group still needs its layer heading and selection key; only the default group is flat.
    get isGroupedByLayer() {
        return Object.keys(this.state.groupedSuggestions).some(groupKey => groupKey !== this.defaultGroupKey);
    }

    get hasRenewalSuggestions() {
        return Object.values(this.state.groupedSuggestions).some(suggestions => suggestions.length > 0);
    }

    get defaultGroupSuggestions() {
        return this.state.groupedSuggestions[this.defaultGroupKey] ?? [];
    }

    setState(state: RenewalSuggestionsStateDto) {
        this.state = state;
    }

    setModalVisible(modalVisible: boolean) {
        this.modalVisible = modalVisible;
    }

    setControlsVisible(controlsVisible: boolean) {
        this.controlsVisible = controlsVisible;
    }

    setSearchingSuggestions(searchingSuggestions: boolean) {
        this.searchingSuggestions = searchingSuggestions;
    }

    setError(error: string) {
        this.error = error;
    }

    toggleSelectedSuggestion(key: string, suggestion: RenewalSuggestionDto) {
        const selectedSuggestion = this.selectedSuggestions.get(key);

        if (selectedSuggestion && selectedSuggestion === suggestion) {
            this.selectedSuggestions.delete(key);
        } else {
            this.selectedSuggestions.set(key, suggestion);
        }
    }

    isSuggestionSelected(key: string, suggestion: RenewalSuggestionDto): boolean {
        return this.selectedSuggestions.get(key) === suggestion;
    }

    applySelectedSuggestions() {
        if (!this.selectedSuggestions.size) {
            return;
        }

        const contractRenewalNode = this.nodesStore.generalInformationNode?.childNodes.find(
            node => node.id === ChildNodeIds.GeneralInformation.ContractRenewal
        );

        if (InstanceUtils.isLayersNode(contractRenewalNode)) {
            this.applySuggestionsToContractRenewalLayers(contractRenewalNode);
        } else {
            this.applySuggestionToGeneralInformation();
        }
    }

    async initialize() {
        if (!this.isRenewalSupported) {
            return;
        }

        if (this.state.searchDisabled || this.contractSubmissionStore.contractSendingOrSent) {
            return;
        }

        this.hasInitializedControls = true;
        this.setControlsVisible(true);

        if (!this.hasRenewalSuggestions) {
            this.searchRenewalSuggestions();
        }
    }

    async getState() {
        const resp = await this.service.getRenewalSuggestionsState(
            this.appStore.sessionExtension,
            this.appStore.sessionId
        );

        if (resp.isOk()) {
            resp.map(state => this.setState(state));
        }
    }

    async searchRenewalSuggestions() {
        const cancelTokenSource = axios.CancelToken.source();

        try {
            this.setError('');
            this.cancelRenewalSuggestionsRequest();

            const suggestionsRequests = this.createRenewalSuggestionsRequests();

            if (!Object.keys(suggestionsRequests).length) {
                message.info('Please fill in contract data to search renewal suggestions');
                return this.clearSuggestions();
            }

            this.setSearchingSuggestions(true);
            this.cancelTokenSource = cancelTokenSource;

            const resp = await this.service.searchRenewalSuggestions(
                this.appStore.sessionExtension,
                this.appStore.sessionId,
                suggestionsRequests,
                cancelTokenSource.token
            );

            if (!resp.isOk()) {
                this.setError('Failed to retrieve renewal suggestions');
                return this.clearSuggestions();
            }

            resp.map(state => this.setState(state));
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }

            this.setError('Failed to retrieve renewal suggestions');
            this.clearSuggestions();
        } finally {
            if (this.cancelTokenSource === cancelTokenSource) {
                this.cancelTokenSource = null;
                this.setSearchingSuggestions(false);
            }
        }
    }

    async disableRenewalSuggestionsSearch() {
        this.setControlsVisible(false);
        this.setState({ ...this.state, searchDisabled: true });

        const resp = await this.service.disableRenewalSuggestionsSearch(
            this.appStore.sessionExtension,
            this.appStore.sessionId
        );

        if (resp.isOk()) {
            resp.map(state => this.setState(state));
        }
    }

    retryRenewalSuggestionsSearch() {
        this.setControlsVisible(true);
        this.setState({ ...this.state, searchDisabled: false });
        this.searchRenewalSuggestions();
    }

    clearSuggestions() {
        this.selectedSuggestions.clear();
        this.setState({ ...this.state, groupedSuggestions: {} });
    }

    cancelRenewalSuggestionsSearch() {
        this.cancelRenewalSuggestionsRequest();
        this.setSearchingSuggestions(false);
        this.setControlsVisible(false);
    }

    dispose() {
        this.baseCompanyChangeSubscription?.unsubscribe();
        this.disposeReactions();
    }

    private createRenewalSuggestionsRequests(): Record<string, RenewalSuggestionRequest> {
        const generalInformationNode = this.nodesStore.generalInformationNode;
        const limitsNode = this.nodesStore.limitsNode;

        if (!generalInformationNode || !limitsNode) {
            return {};
        }

        if (InstanceUtils.isBasicNode(limitsNode)) {
            const request = RenewalSuggestionsStore.createRenewalSuggestionsRequest(
                generalInformationNode.inputs,
                limitsNode.inputs,
                generalInformationNode.inputs
            );

            return request ? { [this.defaultGroupKey]: request } : {};
        }

        if (InstanceUtils.isLayersNode(limitsNode)) {
            const layerRequests = limitsNode.layers.reduce<Record<string, RenewalSuggestionRequest>>((acc, layer) => {
                const request = RenewalSuggestionsStore.createRenewalSuggestionsRequest(
                    generalInformationNode.inputs,
                    layer.inputs,
                    generalInformationNode.inputs
                );

                if (request) {
                    acc[layer.id] = request;
                }

                return acc;
            }, {});

            return layerRequests;
        }

        if (InstanceUtils.isSectionedNode(limitsNode)) {
            const classificationsNode = this.nodesStore.nodes.find(node => node.id === NodeIds.Classifications);
            // Match Main Section only. Missing values must not fall back to child sections
            // or General Information classifications.
            const createMainSectionRequest = (layerId?: string) =>
                RenewalSuggestionsStore.createRenewalSuggestionsRequest(
                    generalInformationNode.inputs,
                    RenewalSuggestionsStore.getMainSectionInputs(limitsNode, layerId),
                    RenewalSuggestionsStore.getMainSectionInputs(classificationsNode, layerId)
                );

            if (InstanceUtils.isSectionsNode(limitsNode)) {
                const request = createMainSectionRequest();

                return request ? { [this.defaultGroupKey]: request } : {};
            }

            // Layer IDs join the matching inputs and route each selection to its renewal fields.
            return limitsNode.layers.reduce<Record<string, RenewalSuggestionRequest>>((acc, layer) => {
                const request = createMainSectionRequest(layer.id);

                if (request) {
                    acc[layer.id] = request;
                }

                return acc;
            }, {});
        }

        return {};
    }

    private applySuggestionsToContractRenewalLayers(contractRenewalNode: LayersNodeModel) {
        contractRenewalNode.layers.forEach(layer => {
            const selectedSuggestion = this.selectedSuggestions.get(layer.id);

            if (selectedSuggestion) {
                RenewalSuggestionsStore.setRenewalInputValues(layer.inputs, selectedSuggestion);
            }
        });
    }

    private applySuggestionToGeneralInformation() {
        const selectedSuggestion = this.selectedSuggestions.get(this.defaultGroupKey);
        const generalInputs = this.nodesStore.generalInformationNode?.inputs;

        if (selectedSuggestion && generalInputs) {
            RenewalSuggestionsStore.setRenewalInputValues(generalInputs, selectedSuggestion);
        }
    }

    private cancelRenewalSuggestionsRequest() {
        const cancelTokenSource = this.cancelTokenSource;
        this.cancelTokenSource = null;

        if (cancelTokenSource) {
            cancelTokenSource.cancel();
        }
    }

    private async handleBaseCompanyChange() {
        this.cancelRenewalSuggestionsRequest();
        this.setSearchingSuggestions(false);
        this.setError('');
        this.clearSuggestions();

        const shouldInitializeSuggestions =
            !this.hasInitializedControls &&
            !this.state.searchDisabled &&
            !this.contractSubmissionStore.contractSendingOrSent;

        const shouldRefreshSuggestions =
            this.controlsVisible && !this.state.searchDisabled && !this.contractSubmissionStore.contractSendingOrSent;

        if ((!shouldInitializeSuggestions && !shouldRefreshSuggestions) || !this.isRenewalSupported) {
            return;
        }

        if (shouldInitializeSuggestions) {
            await this.initialize();
            return;
        }

        await this.searchRenewalSuggestions();
    }
}
